import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Room from "../../types/core/Room";

// Define the type for our state store entry
type RoomState = {
    archives: Room[];
    rooms: Room[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: RoomState = {
    archives: [],
    rooms: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const roomSlice = createSlice({
    name: "room",
    initialState: initialState,
    reducers: {
        resetRoomStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.room?.archives?.map(
                            c => new Room(c),
                        ) || [],
                    rooms:
                        rawData?.payload?.room?.rooms?.map(c => new Room(c)) ||
                        [],
                    status: !rawData?.payload?.room?.rooms
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchRooms.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchRooms.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.rooms = action.payload[0].map(c => new Room(c));
                state.archives = action.payload[1].map(c => new Room(c));
            })
            .addCase(fetchRooms.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addRoom.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addRoom.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.rooms = state.rooms.concat(
                    action.payload.map(c => new Room(c)),
                );
            })
            .addCase(addRoom.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateRoom.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateRoom.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const roomIds = action.payload.map(c => c.id);
                state.rooms = state.rooms
                    .filter(c => !roomIds.includes(c.id))
                    .concat(action.payload.map(c => new Room(c)));
            })
            .addCase(updateRoom.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveRoom.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveRoom.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const roomIds = action.payload.map(c => c.id);
                state.rooms = state.rooms.filter(c => !roomIds.includes(c.id));
                state.archives = state.archives.concat(
                    action.payload.map(c => new Room(c)),
                );
            })
            .addCase(archiveRoom.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetRoomStatus } = roomSlice.actions;

export const initialiseRoomDetails = createAsyncThunk(
    "room/initialiseRoomDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchRooms()),
);

// This is a thunk, they are used to load the state
export const fetchRooms = createAsyncThunk(
    "room/fetchRooms",
    () =>
        Promise.all([
            Datastore.getType<Room[]>("api/Rooms"),
            Datastore.getType<Room[]>("api/Rooms/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.room.status === "idle",
    },
);
export const addRoom = createAsyncThunk(
    "room/addRoom",
    (payload: Room[]) => Datastore.postType<Room[]>("api/Rooms", payload),
    {
        condition: (_, api) =>
            store.getState().db.room.status === "succeeded",
    },
);
export const updateRoom = createAsyncThunk(
    "room/updateRoom",
    (payload: Room[]) => Datastore.putType<Room[]>("api/Rooms", payload),
    {
        condition: (_, api) =>
            store.getState().db.room.status === "succeeded",
    },
);
export const archiveRoom = createAsyncThunk(
    "room/archiveRoom",
    (payload: Room[]) => {
        Datastore.post(
            "api/Rooms/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.room.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importRoomsFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Rooms/Import/TD/${id}`,
    );

    store.dispatch(resetRoomStatus());
    await unwrappedDispatch(fetchRooms());

    return result;
};

// We have to export the reducer as this is added to the state store
export default roomSlice.reducer;
